import { useCallback, useEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

const fields = [
  { id: "company", label: "Company name", placeholder: "Company name", type: "text" },
  { id: "node", label: "Node / process", placeholder: "Node / process (e.g. 16nm, 5nm)", type: "text" },
  { id: "email", label: "Work email", placeholder: "Work email", type: "email" },
] as const

export function CloseSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const labelRef = useRef<HTMLParagraphElement>(null)
  const headlineRef = useRef<HTMLHeadingElement>(null)
  const subtextRef = useRef<HTMLParagraphElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const footerRef = useRef<HTMLParagraphElement>(null)
  const [formData, setFormData] = useState({ company: "", node: "", email: "" })
  const [status, setStatus] = useState<{ type: "idle" | "success" | "error"; message: string }>({
    type: "idle",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      const items = [
        labelRef.current,
        headlineRef.current,
        subtextRef.current,
        ...(formRef.current?.querySelectorAll("[data-close-item]") ?? []),
        footerRef.current,
      ].filter(Boolean)

      gsap.fromTo(
        items,
        { y: 36, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.85,
          stagger: 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 82%",
            toggleActions: "play none none reverse",
          },
        },
      )
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  const handleChange = useCallback((field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const company = formData.company.trim()
      const node = formData.node.trim()
      const email = formData.email.trim()

      if (!company || !node || !email) {
        setStatus({ type: "error", message: "Please fill all fields before submitting." })
        return
      }

      setIsSubmitting(true)
      setStatus({ type: "idle", message: "" })

      try {
        const response = await fetch("https://formsubmit.co/ajax/contact@aexiomsemiconductors.com", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            company,
            node,
            email,
            _subject: "Book a Consultation — AEXIOM Tape-Out Inquiry",
            _template: "table",
            _autoresponse: "Thank you for your inquiry. We will respond shortly.",
          }),
        })

        if (!response.ok) {
          throw new Error("Email service failed. Please try again later.")
        }

        setStatus({ type: "success", message: "Your consultation request was sent successfully." })
        setFormData({ company: "", node: "", email: "" })
      } catch (error) {
        setStatus({ type: "error", message: "Unable to send message. Please try again later." })
      } finally {
        setIsSubmitting(false)
      }
    },
    [formData],
  )

  return (
    <section
      ref={sectionRef}
      id="close"
      className="relative border-t border-border/30 py-32 pl-6 md:pl-28 pr-6 md:pr-12"
    >
      <div className="mx-auto flex max-w-xl flex-col items-center text-center">
        <p ref={labelRef} className="font-mono text-[10px] uppercase tracking-[0.35em] text-accent">
          Close
        </p>

        <h2
          ref={headlineRef}
          className="mt-6 font-[var(--font-bebas)] text-5xl leading-[1.05] tracking-tight uppercase md:text-6xl lg:text-7xl"
        >
          Let&apos;s Scope Your
          <br />
          <span className="text-accent">Next Tape</span>
          <span className="text-foreground">-Out</span>
        </h2>

        <p ref={subtextRef} className="mt-6 max-w-md font-mono text-xs leading-relaxed text-muted-foreground md:text-sm">
          Tell us your node, schedule and scope — we&apos;ll propose a dedicated team within days.
        </p>

        <form ref={formRef} onSubmit={handleSubmit} className="mt-12 w-full space-y-4">
          {fields.map((field) => (
            <div key={field.id} data-close-item>
              <label htmlFor={field.id} className="sr-only">
                {field.label}
              </label>
              <input
                id={field.id}
                name={field.id}
                type={field.type}
                value={formData[field.id]}
                onChange={(event) => handleChange(field.id, event.target.value)}
                placeholder={field.placeholder}
                className={cn(
                  "w-full border border-border/80 bg-secondary/60 px-4 py-4 font-mono text-sm text-foreground",
                  "placeholder:text-muted-foreground",
                  "shadow-[inset_0_0_0_1px_oklch(1_0_0_/_0.04)]",
                  "transition-colors duration-300 outline-none",
                  "hover:border-muted-foreground/50 hover:bg-secondary/80",
                  "focus:border-accent focus:bg-secondary/90 focus:ring-1 focus:ring-accent/40",
                )}
              />
            </div>
          ))}

          <button
            type="submit"
            data-close-item
            disabled={isSubmitting}
            className={cn(
              "group w-full bg-accent px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-accent-foreground",
              "transition-all duration-300",
              "hover:bg-accent/90 hover:shadow-[0_0_32px_oklch(0.7_0.2_45_/_0.35)]",
              isSubmitting && "cursor-not-allowed opacity-80",
            )}
          >
            {isSubmitting ? "Sending..." : "Book a Consultation"}
            <span className="ml-2 inline-block transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
              ↗
            </span>
          </button>

          {status.type !== "idle" ? (
            <p
              className={cn(
                "mt-3 text-center text-sm",
                status.type === "success" ? "text-emerald-400" : "text-rose-400",
              )}
            >
              {status.message}
            </p>
          ) : null}
        </form>

        <p ref={footerRef} className="mt-8 font-mono text-xs text-muted-foreground">
          or email{" "}
          <a
            href="mailto:contact@aexiomsemiconductors.com"
            className="text-accent transition-colors duration-300 hover:text-foreground"
          >
            contact@aexiomsemiconductors.com
          </a>
        </p>
      </div>
    </section>
  )
}
